import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/services', label: 'Services' },
    { href: '/#rooms', label: 'Rooms' },
    { href: '/#pricing', label: 'Pricing' },
    { href: '/#contact', label: 'Contact' },
  ];

  const handleNavClick = (e, href) => {
    if (href.startsWith('/#')) {
      if (isHomePage) {
        e.preventDefault();
        const targetId = href.replace('/#', '');
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0D0D0D]/95 backdrop-blur-md border-b border-[#1a1a1a]' : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <span className="text-white font-heading text-xl tracking-wider">
              CAN<span className="text-[#F5A623]">GGU</span>
            </span>
            <span className="text-gray-500 text-sm font-light tracking-wide">PODCAST</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              link.href.startsWith('/') && !link.href.startsWith('/#') ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-gray-400 hover:text-white transition-colors duration-200 text-sm font-medium tracking-wider"
                >
                  {link.label.toUpperCase()}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-gray-400 hover:text-white transition-colors duration-200 text-sm font-medium tracking-wider"
                >
                  {link.label.toUpperCase()}
                </a>
              )
            ))}
          </nav>

          {/* Book Now Button */}
          <div className="hidden md:block">
            <Link
              to="/booking"
              className="px-8 py-3 bg-[#F5A623] text-black font-semibold text-sm tracking-wider hover:bg-[#FFB84D] transition-all duration-200"
              data-testid="header-book-now"
            >
              BOOK NOW
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#0D0D0D] border-t border-[#1a1a1a] py-6 animate-fade-in-up">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                link.href.startsWith('/') && !link.href.startsWith('/#') ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-gray-400 hover:text-white hover:bg-[#1a1a1a] transition-colors duration-200 text-base font-medium tracking-wider px-4 py-3"
                  >
                    {link.label.toUpperCase()}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-gray-400 hover:text-white hover:bg-[#1a1a1a] transition-colors duration-200 text-base font-medium tracking-wider px-4 py-3"
                  >
                    {link.label.toUpperCase()}
                  </a>
                )
              ))}
              <Link
                to="/booking"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mx-4 mt-4 px-6 py-3 bg-[#F5A623] text-black font-semibold text-center tracking-wider"
                data-testid="mobile-book-now"
              >
                BOOK NOW
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
